import React, {  useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faEdit, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api/api';

import PromoCodeModal from '../../components/PromoCodeModal';
import LoadingLogo from '../../components/Loading';



const PromoCode = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editData, setEditPromoCode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 1
  });


  // Fetch promo codes on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [filter, currentPage]);

  // Fetch promo codes from the backend with search, filter, and pagination
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/promo-codes`, {
        params: {
          search: searchTerm,
          filter,
          page: currentPage,
        },
      });
      setData(response.data); // Set the promo codes
      setMeta(response.meta); // Update pagination metadata
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch promo codes.');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this promo code?')) {
      try {
        await api.delete(`/promo-codes/${id}`);
        toast.success('Promo code deleted successfully.');
        fetchData(); // Refresh the table after deletion
      } catch (error) {
        toast.error('Failed to delete promo code.');
      }
    }
  };

  const handleEdit = (promoCode) => {
    setEditPromoCode(promoCode);
    setIsModalOpen(true); // Show modal for editing
  };

  const handleAdd = () => {
    setEditPromoCode(null); // Reset any edit data
    setIsModalOpen(true); // Show modal for adding
  };

  const handleSave = () => {
    setIsModalOpen(false); // Hide modal after saving
    fetchData(); // Refresh list
  };

  const handleCancel = () => {
    setIsModalOpen(false); // Hide modal without saving
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (meta.current_page < meta.last_page) {
      setCurrentPage(meta.current_page + 1);
    }
  };

  const handlePrevPage = () => {
    if (meta.current_page > 1) {
      setCurrentPage(meta.current_page - 1);
    }
  };

  // Render individual page buttons
  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= meta.last_page; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded-lg border border-tblue ${i === meta.current_page ? 'bg-tblue text-white' : 'bg-white text-gray-700'
            } hover:bg-tblue hover:text-white mx-1`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  if (loading) {
    return <LoadingLogo/>;
  }

  return (
    <div className="mx-auto px-4  max-w-full overflow-x-hidden">
      {/* Header and Actions - Stack on mobile */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 space-y-4 md:space-y-0">
        <h1 className="text-tblue text-3xl font-bold">Promo Codes</h1>
        <button
          onClick={handleAdd}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center hover:bg-accent w-full md:w-auto justify-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add New Promo Code
        </button>
      </div>

      {/* Search and Filter - Stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0 mb-6">
        {/* Search Input */}
        <div className="flex-1 flex items-center border border-gray-300 rounded-lg p-2 shadow-md">
          <FontAwesomeIcon
            icon={faSearch}
            className="text-gray-400 mr-2 transition-transform duration-300 hover:scale-110"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchData();
              }
            }}
            className="outline-none focus:ring-0 bg-transparent w-full text-gray-700 placeholder-gray-400 focus:placeholder-gray-500 focus:text-black"
          />
        </div>

        {/* Filter Dropdown */}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border rounded-lg p-2 shadow-sm w-full sm:w-auto"
        >
          <option value="all">All Promo Codes</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table section with horizontal scroll */}
      <div className="overflow-x-auto relative  rounded-lg">
        <table className="min-w-full bg-white rounded-lg shadow-md">
          <thead className="bg-primary text-white">
            <tr>
              <th className="py-4 px-2 text-left sticky left-0 bg-primary z-2 border-r border-white">Code</th>
              <th className="py-4 px-2 text-left">Active</th>
              <th className="py-4 px-2 text-left">On Dashboard</th>
              <th className="py-4 px-2 text-left hidden md:table-cell">Description</th>
              <th className="py-4 px-2 text-left">Type</th>
              <th className="py-4 px-2 text-left">Value</th>
              <th className="py-4 px-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((promo) => (
                <tr key={promo.code} className="hover:bg-gray-50">
                  <td className="py-2 px-2 font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-white z-2 border-r border-gray-200">
                    {promo.code}
                  </td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-1 rounded-lg text-sm ${
                      promo.is_active 
                        ? "bg-green-100 text-green-600" 
                        : "bg-red-100 text-red-600"
                    }`}>
                      {promo.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <span className={`px-2 py-1 rounded-lg text-sm ${
                      promo.on_dashboard 
                        ? "bg-green-100 text-green-600" 
                        : "bg-red-100 text-red-600"
                    }`}>
                      {promo.on_dashboard ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="py-2 px-2 hidden md:table-cell">{promo.description}</td>
                  <td className="py-2 px-2 capitalize">{promo.discount_type}</td>
                  <td className="py-2 px-2">{promo.discount_value}</td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleEdit(promo)}
                        className="text-blue-500 hover:text-blue-700 p-2"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleDelete(promo.id)}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="py-4 text-center text-gray-500">
                  No promo codes available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination section */}
      <div className="mt-4 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
        <span className="text-gray-500 text-sm">
          Showing {meta.from} to {meta.to} of {meta.total} promo codes
        </span>
        <div className="flex items-center space-x-2 overflow-x-auto py-2">
          <button
            onClick={handlePrevPage}
            disabled={meta.current_page === 1}
            className={`bg-white px-4 py-2 rounded-lg border border-tblue ${
              meta.current_page === 1
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-400"
            }`}
          >
            Previous
          </button>
          
          <div className="flex space-x-2 overflow-x-auto">
            {renderPageNumbers()}
          </div>
          <button
            onClick={handleNextPage}
            disabled={meta.current_page === meta.last_page}
            className={`bg-white px-4 py-2 rounded-lg border border-tblue ${
              meta.current_page === meta.last_page
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-400"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* CRUD Modal for Adding/Editing */}
      {isModalOpen && (
        <PromoCodeModal
          promoCode={editData}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};

export default PromoCode;

